// src/pages/ExpensePurchaseOrder/EpoReviewModal.js
//
// Batch A (Rounds 3-8) — the reusable, tabbed, INTERACTIVE "Review EPO" window.
//
// Legacy ground truth: Dashboard_ForChatGPT/Projects/epoApprove.vb — one window,
// six tabs (Basic Details, Line Items, Attachments, Comments, Approval Log,
// Payments), money panel, pre-auth limit, "Approved By", action buttons.
//
// Supersedes the Round 2 read-only version. Every action is wired to an
// EXISTING server-authorized endpoint; nothing bypasses a server gate. The
// `permissions` prop only controls which buttons SHOW (convenience, not security).
//
// Endpoints (all already exist; server enforces authorization):
//   GET   Epo/{n}                          header + lines + attachments
//   GET   epo-approvals/{n}/history         approval log (+ legacy comments source)
//   GET   epo/{n}/partial-payment[/total]   payments list + total
//   GET   OrgChart/{empId}                  name resolution
//   GET   Epo/attachment/{id}  (blob)       authenticated download
//   POST  Epo/{n}/upload                    add attachment (R1 gate + validation)
//   DELETE Epo/attachment/{id}              delete attachment (R1 gate)
//   POST  epo/{n}/approve | /reject         approval actions (workflow gate)
//   POST  epo/{n}/receive/all | /line/{ln}  receiving (CanReceiveEPO)
//   POST  epo/{n}/partial-payment[/{id}/void]  add/void payment (R19 finance gate)
//   POST  api/epo/{n}/payment { paid }      mark paid / processed (R17 finance gate)
//
// COMMENTS: legacy stores EPO comments AS EpoApprovals rows (epoDB.vb
// AddToApproverList + the Approval Log query). No separate table, no dedicated
// add/edit/delete-comment endpoint yet. The Comments tab READS comment-bearing
// rows; add/edit/delete ships with this batch's small backend note (reuses
// EpoApprovals — no schema change). Until deployed, comments are read-only.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Nav, NavItem, NavLink, TabContent, TabPane,
    Row, Col, Badge, Table, Spinner, Button, Input, Label,
} from "reactstrap";
import classnames from "classnames";
import api from "../../api";
// Rounds 2 batch — modal Submit sends the same PDF approval email as the form flow.
import { getEpoPdfBase64 } from "./epoToPdf";
import { epoErrorMessage } from "./epoErrors";  // Batch 5 / Round 13 — consistent error text
import { downloadEpoPdf, legacyAwareLineTotal } from "./epoToPdf";   // Go-live parity (2026-09-24): EPO#S16 line totals
// Round EPO-Final — legal-entity options/labels (one source with the PDF).
import { EPO_COMPANY_OPTIONS, getEpoCompanyDisplayLabel, normalizeEpoCompanyCode } from "./epoCompanyBranding";
import useBackCloses from "../../hooks/useBackCloses";
import { useTrailLeaf } from "../../Components/Common/DashboardKit/PageHeaderTrailContext";

const fmtDate = (v) => { if (!v) return "—"; const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toLocaleDateString(); };
const fmtDateTime = (v) => { if (!v) return "—"; const d = new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(); };
const money = (n) => { if (n === null || n === undefined || n === "") return "$0.00"; const num = Number(n); return Number.isNaN(num) ? String(n) : num.toLocaleString(undefined, { style: "currency", currency: "USD" }); };
const pick = (obj, ...keys) => { if (!obj) return undefined; for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k]; return undefined; };

// Batch 2 / Round 5 — friendly label for the receiving-history Event column.
const recvEventLabel = (t) => {
    switch (String(t || "").toUpperCase()) {
        case "RECEIVE_ALL": return <Badge color="success" pill>Receive All</Badge>;
        case "REOPEN": return <Badge color="warning" pill>Reopened</Badge>;
        case "OVER_RECEIVE_BLOCKED": return <Badge color="danger" pill>Blocked (over)</Badge>;
        case "RECEIVE": return <Badge color="info" pill>Receive</Badge>;
        default: return t ? <Badge color="light" pill>{t}</Badge> : "—";
    }
};

// Batch 1 / Round 3 — line receiving status badge, shared by the modal's Line
// Items table (display + edit rows). Derives from the running QtyRec vs Quan so
// it shows Partial (x/y) instead of a misleading "received" checkbox.
const lineRecvStatusBadge = (ln) => {
    const ordered = Number(pick(ln, "quan", "Quan") || 0);
    const got = Number(pick(ln, "qtyRec", "QtyRec") || 0);
    const recvd = !!pick(ln, "recvd", "Recvd");
    if (recvd && got >= ordered && ordered > 0) {
        return <Badge color="success">Received</Badge>;
    }
    if (got > 0 && got < ordered) {
        return <Badge color="warning">Partial ({got}/{ordered})</Badge>;
    }
    if (recvd && ordered === 0) {
        return <Badge color="success">Received</Badge>;
    }
    return <Badge color="secondary">Pending</Badge>;
};
const asArray = (v) => { if (Array.isArray(v)) return v; if (v && Array.isArray(v.$values)) return v.$values; if (v && Array.isArray(v.rows)) return v.rows; if (v && Array.isArray(v.results)) return v.results; return []; };
const statusBadge = (s) => {
    const v = String(s || "").toUpperCase();
    if (v.includes("APPROVED")) return <Badge color="success">APPROVED</Badge>;
    if (v.includes("REJECT") || v.includes("DENIED")) return <Badge color="danger">{v}</Badge>;
    if (v.includes("VOID")) return <Badge color="dark">VOID</Badge>;
    if (v.includes("DRAFT")) return <Badge color="secondary">DRAFT</Badge>;
    if (v.includes("PENDING")) return <Badge color="warning">{v}</Badge>;
    if (v.includes("SUBMITTED")) return <Badge color="info">SUBMITTED</Badge>;
    return <Badge color="secondary">{v || "UNKNOWN"}</Badge>;
};
const yesNo = (b) => (b ? <Badge color="success">Yes</Badge> : <Badge color="secondary">No</Badge>);const getFullName = (emp) => { const fn = pick(emp, "firstName", "FirstName") ?? ""; const ln = pick(emp, "lastName", "LastName") ?? ""; return `${fn} ${ln}`.trim() || pick(emp, "name", "Name") || ""; };
const myEmpId = () => { try { const au = JSON.parse(sessionStorage.getItem("authUser") || "{}"); return String(au?.empId ?? au?.EmpId ?? au?.empID ?? ""); } catch { return ""; } };

const TABS = [
    { id: "basic", label: "Basic Details" },
    { id: "lines", label: "Line Items" },
    { id: "attachments", label: "Attachments" },
    { id: "comments", label: "Comments" },
    { id: "approvalLog", label: "Approval Log" },
    { id: "payments", label: "Payments" },
];

export default function EpoReviewModal({
    isOpen, toggle, epoNumber,
    onActionDone, initialTab = "basic", permissions = {}, onNavigate,
}) {
    // Owner 2026-10-12: the browser's Back button closes the EPO instead of
    // leaving the list it was opened from.
    useBackCloses(isOpen, toggle);
    useTrailLeaf(isOpen && epoNumber ? [{ label: `EPO #${epoNumber}` }] : null, 5);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState("");
    const [notice, setNotice] = useState("");

    const [payload, setPayload] = useState(null);
    const [createdByEmp, setCreatedByEmp] = useState(null);
    const [approvedByName, setApprovedByName] = useState("");
    // Batch — resolve EmpIDs to names in the Approval Log (legacy shows names).
    const [nameCache, setNameCache] = useState({});
    const [preAuthLimit, setPreAuthLimit] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    const [history, setHistory] = useState([]);
    // Batch 2 / Round 5 — receiving history (with comments) shown on the Line
    // Items tab. Sourced from GET epo/{n}/receive/log (EpoReceiveDetail-backed),
    // so the modal and the dedicated Receive page show the same history.
    const [recvLog, setRecvLog] = useState([]);
    const [payments, setPayments] = useState([]);
    const [paymentsTotal, setPaymentsTotal] = useState(0);
    const [tabLoading, setTabLoading] = useState(false);

    const [payLine, setPayLine] = useState("");
    const [payAmount, setPayAmount] = useState("");
    const [payNote, setPayNote] = useState("");

    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");

    const me = myEmpId();
    const markChanged = () => setReloadKey((k) => k + 1);

    useEffect(() => { if (isOpen) { setActiveTab(initialTab || "basic"); setNotice(""); setError(""); } }, [isOpen, initialTab]);

    useEffect(() => {
        if (!isOpen || !epoNumber) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true); setError("");
            setPayload(null); setCreatedByEmp(null); setApprovedByName(""); setPreAuthLimit(null);
            try {
                const res = await api.get(`Epo/${epoNumber}`);
                if (cancelled) return;
                setPayload(res.data || null);
                const header = pick(res.data, "header", "Header") || null;
                const createdBy = header ? String(pick(header, "createdBy", "CreatedBy") || "") : "";
                if (createdBy) api.get(`OrgChart/${createdBy}`).then((r) => !cancelled && setCreatedByEmp(r.data)).catch(() => { });
                // Go-live parity (2026-09-24): EPO#N3 — legacy epoApprove.vb shows
                // "Your Pre-Authorized Limit" = the VIEWER's own limit. Reading the
                // creator's limit answered 403 for everyone but IT Admin (blank).
                if (me) api.get(`EpoLimit/employee-limit/${me}`).then((r) => !cancelled && setPreAuthLimit(pick(r.data, "limit", "Limit"))).catch(() => !cancelled && setPreAuthLimit(null));
            } catch (e) {
                // Go-live parity (2026-09-24): the API now answers 403 when the
                // viewer is not associated with the EPO — say so plainly.
                const st = e?.response?.status;
                if (!cancelled) setError(
                    st === 404 ? `EPO #${epoNumber} was not found.`
                        : st === 403 ? `You do not have access to EPO #${epoNumber}.`
                            : "Could not load this EPO. Please try again.");
            } finally { if (!cancelled) setLoading(false); }
        };
        load();
        return () => { cancelled = true; };
    }, [isOpen, epoNumber, reloadKey]);

    const header = useMemo(() => pick(payload, "header", "Header") || null, [payload]);
    const lines = useMemo(() => { const top = asArray(pick(payload, "lines", "Lines")); return top.length ? top : asArray(pick(header, "lines", "Lines")); }, [payload, header]);

    // Owner authz fix — Finance Edit / Receive visibility comes from the
    // SERVER's viewer flags on GET Epo/{n} (Finance / Babu / IT-Admin only).
    // The legacy `permissions` prop was hardcoded true for everyone in
    // AllTasks; it is now advisory-only — it can HIDE, never GRANT.
    const viewerCanFinanceEdit = !!pick(payload, "viewerCanFinanceEdit", "ViewerCanFinanceEdit") && (permissions.canFinanceEdit !== false);
    // Owner 2026-10-16: Void as legacy — the server says whether THIS viewer may
    // void THIS EPO (anyone who can open it; not paid, not void, not pending with
    // a step already approved by someone else).
    const viewerCanVoid = !!pick(payload, "viewerCanVoid", "ViewerCanVoid");
    const viewerCanReceive = !!pick(payload, "viewerCanReceive", "ViewerCanReceive") && (permissions.canReceive !== false);
    const attachments = useMemo(() => asArray(pick(payload, "attachments", "Attachments")), [payload]);

    const loadHistory = useCallback(async () => {
        if (!epoNumber) return;
        setTabLoading(true);
        try { const res = await api.get(`epo-approvals/${epoNumber}/history`); setHistory(asArray(res.data)); }
        catch { setHistory([]); } finally { setTabLoading(false); }
    }, [epoNumber]);

    const loadPayments = useCallback(async () => {
        if (!epoNumber) return;
        setTabLoading(true);
        try {
            const [l, t] = await Promise.all([api.get(`epo/${epoNumber}/partial-payment`), api.get(`epo/${epoNumber}/partial-payment/total`)]);
            setPayments(asArray(l.data)); setPaymentsTotal(Number(pick(t.data, "total", "Total") || 0));
        } catch { setPayments([]); setPaymentsTotal(0); } finally { setTabLoading(false); }
    }, [epoNumber]);

    const loadComments = useCallback(async () => {
        if (!epoNumber) return;
        setTabLoading(true);
        try { const res = await api.get(`epo/${epoNumber}/comments`); setComments(asArray(res.data)); }
        catch { setComments([]); } finally { setTabLoading(false); }
    }, [epoNumber]);

    // Batch 2 / Round 5 — load the receiving history (with comments). Best-effort:
    // a failure leaves the panel empty rather than blocking the Line Items tab.
    const loadRecvLog = useCallback(async () => {
        if (!epoNumber) return;
        try { const res = await api.get(`epo/${epoNumber}/receive/log`); setRecvLog(asArray(res.data)); }
        catch { setRecvLog([]); }
    }, [epoNumber]);

    const addComment = () => run("addComment", async () => {
        if (!newComment.trim()) throw { response: { data: { Message: "Enter a comment." } } };
        await api.post(`epo/${epoNumber}/comments`, { Comment: newComment.trim() });
        setNewComment("");
        await loadComments();
    }, "Comment added.");

    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const startEditComment = (c) => { setEditingId(pick(c, "id", "Id")); setEditText(pick(c, "comment", "Comment") || ""); };
    const cancelEditComment = () => { setEditingId(null); setEditText(""); };
    const saveEditComment = () => run("editComment", async () => {
        if (!editText.trim()) throw { response: { data: { Message: "Comment cannot be empty." } } };
        await api.put(`epo/${epoNumber}/comments/${editingId}`, { Comment: editText.trim() });
        setEditingId(null); setEditText("");
        await loadComments();
    }, "Comment updated.");
    const deleteComment = (c) => run(`delc-${pick(c, "id", "Id")}`, async () => {
        await api.delete(`epo/${epoNumber}/comments/${pick(c, "id", "Id")}`);
        await loadComments();
    }, "Comment deleted.");

    useEffect(() => {
        if (!isOpen) return;
        if (activeTab === "approvalLog") loadHistory();
        if (activeTab === "comments") loadComments();
        if (activeTab === "payments") loadPayments();
        if (activeTab === "lines") loadRecvLog();   // Batch 2 / Round 5
    }, [activeTab, isOpen, reloadKey]);

    // Load approval history whenever the modal opens (not only when the Approval
    // Log tab is active) so the header "Approved By" shows immediately.
    useEffect(() => {
        if (isOpen && epoNumber) loadHistory();
    }, [isOpen, epoNumber, reloadKey, loadHistory]);

    useEffect(() => {
        if (!isOpen || !epoNumber) return;
        let c = false;
        api.get(`epo/${epoNumber}/partial-payment/total`).then((r) => !c && setPaymentsTotal(Number(pick(r.data, "total", "Total") || 0))).catch(() => !c && setPaymentsTotal(0));
        return () => { c = true; };
    }, [isOpen, epoNumber, reloadKey]);

    useEffect(() => {
        // Round 5 batch — prefer the server-resolved name on the final APPROVED
        // row (ApproverName/ActorName from the history DTO); OrgChart is only a
        // fallback when the DTO carries no name.
        if (!history.length) return;
        const row = [...history].filter((r) => {
            // EXACT match — "PENDING_APPROVAL" also CONTAINS "APPROV", which
            // showed the pending approver as "Approved By" on unapproved EPOs.
            const rowStat = String(pick(r, "status", "Status") || "").toUpperCase();
            return rowStat === "APPROVED";
        }).sort((a, b) => new Date(pick(b, "stamp", "Stamp")) - new Date(pick(a, "stamp", "Stamp")))[0];
        if (!row) return;
        const nm = pick(row, "approverName", "ApproverName") || pick(row, "actorName", "ActorName");
        if (nm) { setApprovedByName(String(nm)); return; }
        const id = String(
            pick(row, "approverEmpId", "ApproverEmpId")
            ?? pick(row, "actorEmpId", "ActorEmpId")
            ?? pick(row, "empid", "empId", "EmpId")
            ?? ""
        ).trim();
        if (id) api.get(`OrgChart/${id}`).then((r) => setApprovedByName(getFullName(r.data) || id)).catch(() => setApprovedByName(id));
    }, [history]);

    // Resolve every EmpID that appears in the approval log AND comments to a
    // display name. Looks up only ids not already cached; falls back to the id
    // if a lookup fails. Mirrors the OrgChart resolution used elsewhere.
    useEffect(() => {
        const sources = [...history, ...comments];
        // Go-live parity (2026-09-24): EPO#N5 — line "Received By" holds a NAME on
        // legacy rows but an EmpID on dashboard rows; resolve the numeric ones.
        const recvIds = lines
            .map((ln) => String(pick(ln, "recvdBy", "RecvdBy") || "").trim())
            .filter((x) => /^\d+$/.test(x));
        if (!sources.length && !recvIds.length) return;
        const ids = [...new Set(sources
            .map((r) => String(pick(r, "empid", "empId", "EmpId") || "").trim())
            .concat(recvIds)
            .filter((x) => x && !(x in nameCache)))];
        if (ids.length === 0) return;
        let cancelled = false;
        Promise.all(ids.map((id) =>
            api.get(`OrgChart/${id}`)
                .then((r) => [id, getFullName(r.data) || id])
                .catch(() => [id, id])
        )).then((pairs) => {
            if (cancelled) return;
            setNameCache((prev) => {
                const next = { ...prev };
                for (const [id, nm] of pairs) next[id] = nm;
                return next;
            });
        });
        return () => { cancelled = true; };
    }, [history, comments, lines]);
    // Go-live parity (2026-09-24): EPO#N5 — display name for a line's RecvdBy.
    const recvdByLabel = (ln) => { const v = String(pick(ln, "recvdBy", "RecvdBy") || "").trim(); return v ? (nameCache[v] || v) : "—"; };


    const totals = useMemo(() => {
        const miles = Number(pick(header, "milesTraveled", "MilesTraveled") || 0);
        const rate = Number(pick(header, "mileageRate", "MileageRate") || 0);
        const ship = Number(pick(header, "shippingCharges", "ShippingCharges") || 0);
        const sub = lines.reduce((a, ln) => a + legacyAwareLineTotal(ln, header), 0);   // Go-live parity (2026-09-24): EPO#S16
        const mileage = miles * rate;
        // Round EPO-32 (owner): totals are DYNAMIC — always recomputed from
        // the live line items (+ mileage + shipping). The stored ApproxTotal
        // is only a fallback when no lines have loaded yet; previously it
        // took precedence, so Finance edits to qty/price never moved the
        // Grand Total until a full reload.
        const liveGrand = sub + mileage + ship;
        const grand = (lines && lines.length > 0)
            ? liveGrand
            : Number(pick(header, "approxTotal", "ApproxTotal") || liveGrand);
        return { subtotal: sub, mileageAllowance: mileage, shipping: ship, grandTotal: grand, paid: paymentsTotal, remaining: Math.max(grand - paymentsTotal, 0) };
    }, [header, lines, paymentsTotal]);

    const run = async (name, fn, successMsg) => {
        setBusy(name); setError(""); setNotice("");
        try {
            await fn();
            // Round EPO-Final — successMsg may be a function so an action can
            // tailor its message from the server response (e.g. addPayment
            // reports the automatic Paid transition). All existing string
            // callers behave exactly as before.
            const msg = typeof successMsg === "function" ? successMsg() : successMsg;
            if (msg) setNotice(msg);
            markChanged();
            onActionDone && onActionDone();
        } catch (e) {
            if (e && e.__silent) { setBusy(""); return; }  // user cancelled a confirm/prompt — no error
            // Batch 5 / Round 13 — use the shared helper so backend messages
            // (over-receive, not-approved, comment-required, ProblemDetails,
            // network) surface consistently across all EPO screens.
            setError(epoErrorMessage(e, "Action failed.", {
                403: "You are not authorized to perform this action.",
            }));
        } finally { setBusy(""); }
    };

    const downloadAttachment = async (att) => {
        const id = pick(att, "id", "Id");
        const name = pick(att, "fileName", "FileName") || `attachment-${id}`;
        try {
            const res = await api.get(`Epo/attachment/${id}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch { setError("Could not download the attachment."); }
    };
    const uploadAttachment = (file) => run("upload", async () => {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`Epo/${epoNumber}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    }, "Attachment uploaded.");
    const deleteAttachment = (att) => run("delAtt", async () => { await api.delete(`Epo/attachment/${pick(att, "id", "Id")}`); }, "Attachment deleted.");

    const approve = () => run("approve", async () => { const c = window.prompt("Optional approval comment:", "") ?? ""; await api.post(`epo/${epoNumber}/approve`, { Comment: c }); }, "EPO approved.");
    const reject = () => run("reject", async () => { const c = window.prompt("Rejection comment (required):", ""); if (!c) throw { response: { data: { Message: "A rejection comment is required." } } }; await api.post(`epo/${epoNumber}/reject`, { Comment: c }); }, "EPO rejected.");

    const receiveAll = () => run("receiveAll", async () => {
        // Round 3 — Receive All now explains exactly what it does and confirms,
        // matching the dedicated Receive page. Cancelling is silent (no error).
        if (!window.confirm("Receive All will mark every line's REMAINING quantity as received and close the order. Continue?")) {
            throw { __silent: true };
        }
        await api.post(`epo/${epoNumber}/receive/all`, {});
    }, "All items marked received.");
    const receiveLine = (ln) => run(`recv-${ln}`, async () => { await api.post(`epo/${epoNumber}/receive/line/${ln}`, {}); }, `Line ${ln} marked received.`);
    const reopenLine = (ln) => run(`reopen-${ln}`, async () => { await api.post(`epo/${epoNumber}/receive/line/${ln}/reopen`, {}); }, `Line ${ln} reopened.`);

    // Batch 1 / Round 3 — partial receiving in the modal, synced with the
    // dedicated Receive page (both call POST .../receive/line/{n}/qty-receive).
    // recvDraft holds the in-flight "qty received now" + comment per line so the
    // inputs are controlled without re-fetching on every keystroke. The backend
    // enforces the after-approval guard, the over-receive hard block, and the
    // required-comment-on-partial rule; we surface its 400 message verbatim.
    const [recvDraft, setRecvDraft] = useState({}); // { [lineNum]: { qty: "1", comment: "" } }
    const setRecvQty = (ln, val) =>
        setRecvDraft((d) => ({ ...d, [ln]: { ...(d[ln] || {}), qty: val } }));
    const setRecvComment = (ln, val) =>
        setRecvDraft((d) => ({ ...d, [ln]: { ...(d[ln] || {}), comment: val } }));

    const receiveQtyLine = (ln, ordered, alreadyRec) => {
        const draft = recvDraft[ln] || {};
        const n = Number(draft.qty);
        if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
            setError(`Line ${ln}: enter a positive whole number for quantity received.`);
            return;
        }
        const remaining = Number(ordered) - Number(alreadyRec || 0);
        // Client-side hint only; the server is the source of truth and will block.
        if (remaining >= 0 && n > remaining) {
            setError(
                `Line ${ln}: received quantity ${n} exceeds the remaining ${remaining} ` +
                `(ordered ${ordered}, already received ${alreadyRec || 0}). Ask the employee to create a new EPO ` +
                `for the extra items, or have Finance/IT Admin update the EPO quantity.`
            );
            return;
        }
        const willBeFull = Number(alreadyRec || 0) + n >= Number(ordered);
        const comment = (draft.comment || "").trim();
        if (!willBeFull && !comment) {
            setError(`Line ${ln}: a comment is required when receiving a partial quantity.`);
            return;
        }
        run(`recvqty-${ln}`, async () => {
            await api.post(`epo/${epoNumber}/receive/line/${ln}/qty-receive`, {
                QtyReceived: n,
                Comment: comment,
            });
            // Clear the row draft after a successful save (load() refreshes values).
            setRecvDraft((d) => { const c = { ...d }; delete c[ln]; return c; });
        }, willBeFull ? `Line ${ln} fully received.` : `Line ${ln}: received ${n} (partial).`);
    };

    const deleteLine = (ln) => run(`delln-${ln}`, async () => {
        // Go-live parity (2026-09-24): Finance line deletes now save in place
        // (the old "re-approval" left the EPO PENDING with no approver).
        const msg = isApproved
            ? `Delete line ${ln}? This EPO is APPROVED — Finance changes are saved in place (no re-approval) and logged in the Approval Log.`
            : `Delete line ${ln}?`;
        if (!window.confirm(msg)) throw { __silent: true };
        await api.delete(`Epo/${epoNumber}/line/${ln}`);
    }, "Line deleted.");

    // per-line field edit (decision F5) — saves via the dedicated fields endpoint
    const [editLineNum, setEditLineNum] = useState(null);
    const [editLine, setEditLine] = useState(null);
    const startEditLine = (ln) => {
        const lnNum = pick(ln, "lineNum", "LineNum");
        setEditLineNum(lnNum);
        setEditLine({
            description: pick(ln, "description", "Description") || "",
            quan: Number(pick(ln, "quan", "Quan") || 0),
            unitPrice: Number(pick(ln, "unitPrice", "UnitPrice") || 0),
            taxPercent: Number(pick(ln, "taxPercent", "TaxPercent") || 0),
            discountPercent: Number(pick(ln, "discountPercent", "DiscountPercent") || 0),
            asset: !!pick(ln, "asset", "Asset"),
            rd: !!pick(ln, "rd", "RD"),
            productGroup: pick(ln, "productGroup", "ProductGroup") || "",
            dept: pick(ln, "dept", "Dept") || "",
            acct: pick(ln, "acct", "Acct") || "",
            uom: pick(ln, "uom", "Uom") || "",
        });
    };
    const cancelEditLine = () => { setEditLineNum(null); setEditLine(null); };
    const saveEditLine = (ln) => run(`editln-${ln}`, async () => {
        // client-side validation mirroring the server guards (instant feedback)
        const q = Number(editLine.quan), up = Number(editLine.unitPrice);
        const tx = Number(editLine.taxPercent), ds = Number(editLine.discountPercent);
        if ([q, up, tx, ds].some((n) => Number.isNaN(n)))
            throw { response: { data: { Message: "Quantity, price, tax % and discount % must be numbers." } } };
        if (q < 0 || up < 0) throw { response: { data: { Message: "Quantity and unit price cannot be negative." } } };
        if (tx < 0 || ds < 0) throw { response: { data: { Message: "Tax % and discount % cannot be negative." } } };
        // Owner 2026-10-14: the details may not be blanked out on an edit either.
        if (!String(editLine.description || "").trim())
            throw { response: { data: { Message: "Enter the product / service description." } } };
        if (!String(editLine.productGroup || "").trim() || !String(editLine.dept || "").trim() || !String(editLine.acct || "").trim())
            throw { response: { data: { Message: "Product group, department and account number are required on every line." } } };
        if (!(q > 0)) throw { response: { data: { Message: "Enter a quantity greater than zero." } } };
        await api.put(`Epo/${epoNumber}/line/${ln}/fields`, {
            Description: editLine.description,
            Quan: q,
            UnitPrice: up,
            TaxPercent: tx,
            DiscountPercent: ds,
            Asset: !!editLine.asset,
            Rd: !!editLine.rd,
            ProductGroup: editLine.productGroup,
            Dept: editLine.dept,
            Acct: editLine.acct,
            Uom: editLine.uom,
        });
        cancelEditLine();
    }, "Line updated.");

    // Rounds 2 batch — creator submits straight from the Review modal.
    const submitEpo = () => run("submitEpo", async () => {
        // Go-live parity (2026-09-24): EPO#S5 — this modal posts /notify itself;
        // a PDF failure still sends the approver e-mail (without the PDF).
        await api.post(`epo/${epoNumber}/submit`, { clientSendsNotify: true });
        // Best-effort PDF approval email, same as the form flow — a mail
        // failure must never mask a successful submit.
        try {
            let pdfBase64 = "";
            try { pdfBase64 = await getEpoPdfBase64(epoNumber, api); } catch (pdfErr) { console.error("EPO PDF build failed:", pdfErr); }
            await api.post(`Epo/${epoNumber}/notify`, { pdfBase64: pdfBase64 || "", fileName: `EPO-${epoNumber}.pdf` });
        } catch (mailErr) {
            console.error("Submit notify failed:", mailErr);
        }
    });

    const setProcessed = (paid) => run("markPaid", async () => { await api.post(`epo/${epoNumber}/payment`, { Paid: paid }); }, paid ? "EPO marked Processed (Paid)." : "EPO marked NOT Processed.");

    const voidEpo = () => run("voidEpo", async () => {
        if (!window.confirm(`Void EPO #${epoNumber}? This cannot be undone.`)) throw { __silent: true };
        const comment = window.prompt("Reason for voiding (optional):", "") ?? "";
        await api.post(`epo/${epoNumber}/void`, { Comment: comment });
    }, "EPO voided.");

    const cancelEpo = () => run("cancelEpo", async () => {
        if (!window.confirm(`Cancel EPO #${epoNumber}? Only the creator can cancel a pending EPO.`)) throw { __silent: true };
        const comment = window.prompt("Reason for cancelling (optional):", "") ?? "";
        await api.post(`epo/${epoNumber}/cancel`, { Comment: comment });
    }, "EPO cancelled.");

    const [printing, setPrinting] = useState(false);
    const printEpo = async () => {
        setPrinting(true); setError("");
        try { await downloadEpoPdf(epoNumber, api); }
        catch { setError("Could not generate the EPO PDF."); }
        finally { setPrinting(false); }
    };

    // ── Finance Edit (in-modal, header-only) ─────────────────────────────
    const [editMode, setEditMode] = useState(false);
    const [editHeader, setEditHeader] = useState(null);
    const startHeaderEdit = () => {
        setActiveTab("basic");
        setEditHeader({
            company: pick(header, "company", "Company") || "",
            // Round EPO-Final — normalized; "" = Legacy/Unassigned (unchanged
            // unless Finance actively picks one — PUT omits empty).
            owningCompany: normalizeEpoCompanyCode(pick(header, "owningCompany", "OwningCompany")) || "",
            vendorId: pick(header, "vendorID", "vendorId", "VendorID") || "",
            street1: pick(header, "street1", "Street1") || "",
            street2: pick(header, "street2", "Street2") || "",
            city: pick(header, "city", "City") || "",
            state: pick(header, "state", "State") || "",
            zip: pick(header, "zip", "Zip") || "",
            country: pick(header, "country", "Country") || "",
            phone: pick(header, "phone", "Phone") || "",
            fax: pick(header, "fax", "Fax") || "",
            email: pick(header, "email", "Email") || "",
            epoRemark: pick(header, "epoRemark", "EpoRemark") || "",
            receiptMethod: pick(header, "receiptMethod", "ReceiptMethod") || "",
            paymentType: pick(header, "paymentType", "PaymentType") || "",
            needCheckBy: pick(header, "needCheckBy", "NeedCheckBy") || "",
            noteExternal: pick(header, "noteExternal", "NoteExternal") || "",
            noteInternal: pick(header, "noteInternal", "NoteInternal") || "",
            milesTraveled: Number(pick(header, "milesTraveled", "MilesTraveled") || 0),
            mileageRate: Number(pick(header, "mileageRate", "MileageRate") || 0),
            shippingCharges: Number(pick(header, "shippingCharges", "ShippingCharges") || 0),
            taxRate: Number(pick(header, "taxRate", "TaxRate") || 0),
            sswhiteEmp: !!pick(header, "ssWhiteEmp", "SsWhiteEmp", "sswhiteEmp"),
        });
        setEditMode(true);
    };
    const cancelHeaderEdit = () => { setEditMode(false); setEditHeader(null); };
    const eh = (k, v) => setEditHeader((p) => ({ ...p, [k]: v }));
    const saveHeaderEdit = () => run("saveHeader", async () => {
        const mr = Number(editHeader.mileageRate), tr = Number(editHeader.taxRate);
        const mt = Number(editHeader.milesTraveled), sc = Number(editHeader.shippingCharges);
        if ([mr, tr, mt, sc].some((n) => Number.isNaN(n)))
            throw { response: { data: { Message: "Miles, mileage rate, shipping and tax rate must be numbers." } } };
        if (mr < 0 || tr < 0) throw { response: { data: { Message: "Mileage rate and tax rate cannot be negative." } } };
        if (mt < 0 || sc < 0) throw { response: { data: { Message: "Miles traveled and shipping charges cannot be negative." } } };
        await api.put(`Epo/${epoNumber}/header`, {
            Company: editHeader.company,
            // Round EPO-Final — null while unselected: the backend treats
            // null/empty as "leave unchanged" (never silently clears).
            OwningCompany: editHeader.owningCompany || null,
            VendorId: editHeader.vendorId,
            Street1: editHeader.street1,
            Street2: editHeader.street2,
            City: editHeader.city,
            State: editHeader.state,
            Zip: editHeader.zip,
            Country: editHeader.country,
            Phone: editHeader.phone,
            Fax: editHeader.fax,
            Email: editHeader.email,
            EpoRemark: editHeader.epoRemark,
            ReceiptMethod: editHeader.receiptMethod,
            PaymentType: editHeader.paymentType,
            NeedCheckBy: editHeader.needCheckBy,
            NoteExternal: editHeader.noteExternal,
            NoteInternal: editHeader.noteInternal,
            MilesTraveled: Number(editHeader.milesTraveled),
            MileageRate: Number(editHeader.mileageRate),
            ShippingCharges: Number(editHeader.shippingCharges),
            TaxRate: Number(editHeader.taxRate),
            SswhiteEmp: !!editHeader.sswhiteEmp,
        });
        cancelHeaderEdit();
    }, (isApproved && !viewerCanFinanceEdit) ? "EPO updated — sent back for re-approval." : "EPO updated.");
    // Round EPO-Final — the backend now AUTO-finalizes the EPO as Paid when
    // this payment makes the authoritative remaining liability exactly $0.00
    // (no separate "Mark Processed" click required). The POST response carries
    // the recomputed ledger totals + autoPaid/overpaid flags; run()'s
    // markChanged + onActionDone then refresh the modal AND the parent list,
    // so a fully-paid row drops off Ready for Payment automatically while the
    // modal stays open showing the Paid state.
    const lastPayResult = React.useRef(null);
    const addPayment = () => run("addPay", async () => {
        if (!payLine) throw { response: { data: { Message: "Select a line to pay." } } };
        // Round EPO-Pay-1 (owner request #6): negative amounts are VALID —
        // credit memos / vendor refunds post as negative payments. Only a
        // blank/zero/non-numeric amount is rejected.
        const amt = Number(payAmount);
        if (!payAmount || !Number.isFinite(amt) || amt === 0) throw { response: { data: { Message: "Enter a non-zero payment amount (negative = credit memo / refund)." } } };
        // Go-live parity (2026-09-24): EPO#N2 — legacy required a payment note (server enforces too).
        if (!String(payNote || "").trim()) throw { response: { data: { Message: "Enter a note for this payment (e.g. check number, card or invoice reference)." } } };
        const res = await api.post(`epo/${epoNumber}/partial-payment`, { LineNum: Number(payLine || 0), Amount: amt, Note: payNote || "" });
        lastPayResult.current = res?.data || null;
        setPayLine(""); setPayAmount(""); setPayNote("");
    }, () => {
        const d = lastPayResult.current || {};
        const autoPaid = !!pick(d, "autoPaid", "AutoPaid");
        const overpaid = !!pick(d, "overpaid", "Overpaid");
        const rem = pick(d, "remaining", "Remaining");
        if (autoPaid) return `Payment added — remaining liability is $0.00. EPO #${epoNumber} was automatically marked Paid/Processed.`;
        if (overpaid) return `Payment added — WARNING: active payments now exceed the Grand Total (remaining ${money(rem)}). The EPO was NOT marked Paid.`;
        return "Payment added.";
    });
    const voidPayment = (id) => run(`void-${id}`, async () => { await api.post(`epo/${epoNumber}/partial-payment/${id}/void`, {}); }, "Payment voided.");

    const createdById = header ? String(pick(header, "createdBy", "CreatedBy") || "—") : "—";
    const createdByLabel = createdByEmp ? `${getFullName(createdByEmp)} (${createdById})` : createdById;
    const status = pick(header, "status", "Status");
    const isApproved = String(status || "").toUpperCase().includes("APPROVED");
    const statusU = String(status || "").toUpperCase();
    const isPaid = !!pick(header, "paymentIssued", "PaymentIssued");
    const isVoid = statusU.includes("VOID");
    const isPending = statusU.includes("PENDING");
    const iAmCreator = createdById !== "—" && createdById === me;
    const isDraft = String(status || "").toUpperCase() === "DRAFT";
    const isPendingStatus = String(status || "").toUpperCase() === "PENDING";
    // Rounds 2 batch — the creator may submit a DRAFT, or a legacy PENDING EPO
    // that has no approval rows at all (stuck before the workflow existed).
    const isSubmittableHere = iAmCreator && (isDraft || (isPendingStatus && (history?.length || 0) === 0));

    const iAmPendingApprover = history.some((r) => {
        const active = pick(r, "active", "Active");
        const st = String(pick(r, "status", "Status") || "").toUpperCase();
        const ap = String(pick(r, "approverEmpId", "ApproverEmpId", "empid", "empId") || "");
        return active && st.includes("PENDING") && ap === me && me !== "";
    }) || (
        // Go-live parity (2026-09-24): EPO#S2 — the server says the viewer may
        // decide (the current approver, or a manager above them in the chain).
        !!pick(payload, "viewerCanDecide", "ViewerCanDecide") && isPendingStatus
    );

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable>
            <ModalHeader toggle={toggle}>
                <span className="me-3">Review EPO #{epoNumber}</span>
                {header ? statusBadge(status) : null}
            </ModalHeader>

            <ModalBody>
                {loading ? (
                    <div className="d-flex align-items-center gap-2 py-4"><Spinner size="sm" /> <span className="text-muted">Loading EPO…</span></div>
                ) : !header ? (
                    <div className="alert alert-danger mb-0">{error || "No data found for this EPO."}</div>
                ) : (
                    <>
                        {error && <div className="alert alert-danger py-2">{error}</div>}
                        {notice && <div className="alert alert-success py-2">{notice}</div>}

                        <Row className="mb-3">
                            <Col md={4}>
                                <div className="text-muted small">Created By</div>
                                <div className="fw-semibold">{createdByLabel}</div>
                                <div className="text-muted small mt-1">{fmtDate(pick(header, "createdDate", "CreatedDate"))}</div>
                            </Col>
                            <Col md={4}>
                                <div className="text-muted small">Approved By</div>
                                <div className="fw-semibold">{approvedByName || "—"}</div>
                            </Col>
                            <Col md={4} className="text-md-end">
                                <div className="text-muted small">Your Pre-Authorized Limit</div>
                                <div className="fw-semibold">{preAuthLimit === null || preAuthLimit === undefined ? "—" : money(preAuthLimit)}</div>
                            </Col>
                        </Row>

                        {/* Action row — mirrors the legacy Review EPO / timeline buttons.
                            Finance Edit now edits the EPO IN-PLACE on the Basic Details
                            tab (no redirect to the create form). Receive Items jumps to
                            the Line Items tab. (The old "Partial Payments" button was
                            removed — it only switched to the Payments tab, which is
                            already in the tab bar and now handles per-line payments
                            directly.) All gated by permission props; server still
                            enforces every underlying action. */}
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {viewerCanFinanceEdit && !editMode && (
                                <Button color="info" size="sm" onClick={startHeaderEdit}>
                                    Finance Edit
                                </Button>
                            )}
                            {(viewerCanReceive || viewerCanFinanceEdit) && (
                                <Button color="light" size="sm" onClick={() => setActiveTab("lines")}>
                                    Receive Items
                                </Button>
                            )}
                        </div>

                        <Nav tabs className="mb-3">
                            {TABS.map((t) => (
                                <NavItem key={t.id}>
                                    <NavLink href="#" className={classnames({ active: activeTab === t.id })} onClick={(e) => { e.preventDefault(); setActiveTab(t.id); }}>
                                        {t.label}
                                        {t.id === "attachments" && attachments.length ? <Badge color="light" className="ms-1 text-dark">{attachments.length}</Badge> : null}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>

                        <TabContent activeTab={activeTab}>
                            <TabPane tabId="basic">
                                {editMode && editHeader ? (
                                    <>
                                        <div className="d-flex flex-wrap gap-2 align-items-center mb-3 p-2 border rounded bg-light">
                                            <span className="fw-semibold small text-uppercase text-muted">Finance Edit</span>
                                            <Button color="primary" size="sm" disabled={busy === "saveHeader"} onClick={saveHeaderEdit}>{busy === "saveHeader" ? "Saving…" : "Save EPO"}</Button>
                                            <Button color="light" size="sm" onClick={cancelHeaderEdit}>Cancel</Button>
                                            {isApproved && (
                                                <span className="text-muted small">
                                                    {viewerCanFinanceEdit
                                                        ? "This EPO is APPROVED — Finance changes are saved in place (no re-approval)."
                                                        : "This EPO is APPROVED — saving changes will send it back for re-approval."}
                                                </span>
                                            )}
                                        </div>
                                        <Row>
                                            <Col lg={7}>
                                                <h6 className="text-muted text-uppercase small">EPO Company (Legal Entity)</h6>
                                                <Row className="g-2 mb-3">
                                                    {/* Round EPO-Final — Finance may correct the owning company.
                                                        Select (not free text): only the three legal entities
                                                        exist. On a non-finance creator's APPROVED edit this PUT
                                                        already re-routes for re-approval (material change). */}
                                                    <Col md={8}>
                                                        <Input type="select" bsSize="sm" value={editHeader.owningCompany}
                                                            onChange={(e) => eh("owningCompany", e.target.value)}>
                                                            <option value="">Legacy / Unassigned (leave unchanged)</option>
                                                            {EPO_COMPANY_OPTIONS.map((o) => (
                                                                <option key={o.code} value={o.code}>{o.label}</option>
                                                            ))}
                                                        </Input>
                                                    </Col>
                                                </Row>
                                                <h6 className="text-muted text-uppercase small">Vendor / Payee</h6>
                                                <Row className="g-2 mb-3">
                                                    <Col md={8}><Label className="small text-muted mb-0">Vendor</Label><Input bsSize="sm" value={editHeader.company} onChange={(e) => eh("company", e.target.value)} /></Col>
                                                    <Col md={4}><Label className="small text-muted mb-0">Vendor ID</Label><Input bsSize="sm" value={editHeader.vendorId} onChange={(e) => eh("vendorId", e.target.value)} /></Col>
                                                    <Col md={6}><Label className="small text-muted mb-0">Street 1</Label><Input bsSize="sm" value={editHeader.street1} onChange={(e) => eh("street1", e.target.value)} /></Col>
                                                    <Col md={6}><Label className="small text-muted mb-0">Street 2</Label><Input bsSize="sm" value={editHeader.street2} onChange={(e) => eh("street2", e.target.value)} /></Col>
                                                    <Col md={5}><Label className="small text-muted mb-0">City</Label><Input bsSize="sm" value={editHeader.city} onChange={(e) => eh("city", e.target.value)} /></Col>
                                                    <Col md={3}><Label className="small text-muted mb-0">State</Label><Input bsSize="sm" value={editHeader.state} onChange={(e) => eh("state", e.target.value)} /></Col>
                                                    <Col md={4}><Label className="small text-muted mb-0">Zip</Label><Input bsSize="sm" value={editHeader.zip} onChange={(e) => eh("zip", e.target.value)} /></Col>
                                                    <Col md={4}><Label className="small text-muted mb-0">Country</Label><Input bsSize="sm" value={editHeader.country} onChange={(e) => eh("country", e.target.value)} /></Col>
                                                    <Col md={4}><Label className="small text-muted mb-0">Phone</Label><Input bsSize="sm" value={editHeader.phone} onChange={(e) => eh("phone", e.target.value)} /></Col>
                                                    <Col md={4}><Label className="small text-muted mb-0">Fax</Label><Input bsSize="sm" value={editHeader.fax} onChange={(e) => eh("fax", e.target.value)} /></Col>
                                                    <Col md={12}><Label className="small text-muted mb-0">Email</Label><Input bsSize="sm" value={editHeader.email} onChange={(e) => eh("email", e.target.value)} /></Col>
                                                </Row>
                                                <h6 className="text-muted text-uppercase small">EPO Details</h6>
                                                <Row className="g-2 mb-0">
                                                    <Col md={12}><Label className="small text-muted mb-0">Purpose / Remark</Label><Input bsSize="sm" value={editHeader.epoRemark} onChange={(e) => eh("epoRemark", e.target.value)} /></Col>
                                                    <Col md={6}><Label className="small text-muted mb-0">Receipt Method</Label><Input bsSize="sm" value={editHeader.receiptMethod} onChange={(e) => eh("receiptMethod", e.target.value)} /></Col>
                                                    <Col md={6}><Label className="small text-muted mb-0">Payment Type</Label><Input bsSize="sm" value={editHeader.paymentType} onChange={(e) => eh("paymentType", e.target.value)} /></Col>
                                                    <Col md={6}><Label className="small text-muted mb-0">Days to Pay</Label><Input bsSize="sm" value={editHeader.needCheckBy} onChange={(e) => eh("needCheckBy", e.target.value)} /></Col>
                                                    <Col md={6} className="d-flex align-items-end"><Label className="small text-muted mb-0 me-2">SS White Employee</Label><Input type="checkbox" checked={editHeader.sswhiteEmp} onChange={(e) => eh("sswhiteEmp", e.target.checked)} /></Col>
                                                    <Col md={12}><Label className="small text-muted mb-0">Purchase Notes</Label><Input type="textarea" bsSize="sm" rows={2} value={editHeader.noteExternal} onChange={(e) => eh("noteExternal", e.target.value)} /></Col>
                                                    <Col md={12}><Label className="small text-muted mb-0">Internal Notes</Label><Input type="textarea" bsSize="sm" rows={2} value={editHeader.noteInternal} onChange={(e) => eh("noteInternal", e.target.value)} /></Col>
                                                </Row>
                                            </Col>
                                            <Col lg={5}>
                                                <div className="border rounded p-3 bg-light">
                                                    <h6 className="text-muted text-uppercase small">Charges</h6>
                                                    <Row className="g-2">
                                                        <Col md={6}><Label className="small text-muted mb-0">Miles Traveled</Label><Input bsSize="sm" type="number" value={editHeader.milesTraveled} onChange={(e) => eh("milesTraveled", e.target.value)} /></Col>
                                                        <Col md={6}><Label className="small text-muted mb-0">Mileage Rate</Label><Input bsSize="sm" type="number" step="0.01" value={editHeader.mileageRate} onChange={(e) => eh("mileageRate", e.target.value)} /></Col>
                                                        <Col md={6}><Label className="small text-muted mb-0">Shipping Charges</Label><Input bsSize="sm" type="number" step="0.01" value={editHeader.shippingCharges} onChange={(e) => eh("shippingCharges", e.target.value)} /></Col>
                                                        <Col md={6}><Label className="small text-muted mb-0">Tax Rate</Label><Input bsSize="sm" type="number" step="0.01" value={editHeader.taxRate} onChange={(e) => eh("taxRate", e.target.value)} /></Col>
                                                    </Row>
                                                    <div className="text-muted small mt-2">Line items are edited on the Line Items tab. Saving here updates header details only; the grand total recomputes from the lines.</div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                ) : (
                                <Row>
                                    <Col lg={7}>
                                        <h6 className="text-muted text-uppercase small">Vendor / Payee</h6>
                                        <Table borderless size="sm" className="mb-3"><tbody>
                                            {/* Round EPO-Final — the EPO's OWN legal entity, shown FIRST so
                                                it can't be confused with the vendor row below it. */}
                                            <tr><td className="text-muted" style={{ width: 140 }}>Legal Entity</td><td className="fw-semibold">{getEpoCompanyDisplayLabel(pick(header, "owningCompany", "OwningCompany"))}</td></tr>
                                            <tr><td className="text-muted">Vendor</td><td className="fw-semibold">{pick(header, "company", "Company") || "—"}</td></tr>
                                            <tr><td className="text-muted">Vendor ID</td><td>{pick(header, "vendorID", "vendorId", "VendorID") || "—"}</td></tr>
                                            <tr><td className="text-muted">Address</td><td>{[pick(header, "street1", "Street1"), pick(header, "street2", "Street2")].filter(Boolean).join(", ") || "—"}</td></tr>
                                            <tr><td className="text-muted">City/State/Zip</td><td>{[pick(header, "city", "City"), pick(header, "state", "State"), pick(header, "zip", "Zip")].filter(Boolean).join(", ") || "—"}</td></tr>
                                            <tr><td className="text-muted">Country</td><td>{pick(header, "country", "Country") || "—"}</td></tr>
                                            <tr><td className="text-muted">Phone</td><td>{pick(header, "phone", "Phone") || "—"}</td></tr>
                                            <tr><td className="text-muted">Fax</td><td>{pick(header, "fax", "Fax") || "—"}</td></tr>
                                            <tr><td className="text-muted">Email</td><td>{pick(header, "email", "Email") || "—"}</td></tr>
                                        </tbody></Table>
                                        <h6 className="text-muted text-uppercase small">EPO Details</h6>
                                        <Table borderless size="sm" className="mb-0"><tbody>
                                            <tr><td className="text-muted" style={{ width: 140 }}>Purpose / Remark</td><td>{pick(header, "epoRemark", "EpoRemark") || "—"}</td></tr>
                                            <tr><td className="text-muted">EPO Type</td><td>{pick(header, "epoType", "EpoType") || "—"}</td></tr>
                                            <tr><td className="text-muted">Receipt Method</td><td>{pick(header, "receiptMethod", "ReceiptMethod") || "—"}</td></tr>
                                            <tr><td className="text-muted">Payment Type</td><td>{pick(header, "paymentType", "PaymentType") || "—"}</td></tr>
                                            <tr><td className="text-muted">Days to Pay</td><td>{pick(header, "needCheckBy", "NeedCheckBy") || "—"}</td></tr>
                                            <tr><td className="text-muted">User of Product</td><td>{yesNo(pick(header, "userOfProduct", "UserOfProduct"))}</td></tr>
                                            <tr><td className="text-muted">SS White Employee</td><td>{yesNo(pick(header, "ssWhiteEmp", "SsWhiteEmp"))}</td></tr>
                                            <tr><td className="text-muted">Purchase Notes</td><td>{pick(header, "noteExternal", "NoteExternal") || "—"}</td></tr>
                                            <tr><td className="text-muted">Internal Notes</td><td>{pick(header, "noteInternal", "NoteInternal") || "—"}</td></tr>
                                        </tbody></Table>
                                    </Col>
                                    <Col lg={5}>
                                        <div className="border rounded p-3 bg-light">
                                            <h6 className="text-muted text-uppercase small">Totals</h6>
                                            <Table borderless size="sm" className="mb-3"><tbody>
                                                <tr><td className="text-muted">Subtotal</td><td className="text-end">{money(totals.subtotal)}</td></tr>
                                                <tr><td className="text-muted">Mileage Allowance</td><td className="text-end">{money(totals.mileageAllowance)}</td></tr>
                                                <tr><td className="text-muted">Shipping Charges</td><td className="text-end">{money(totals.shipping)}</td></tr>
                                                <tr className="border-top"><td className="fw-bold">Grand Total</td><td className="text-end fw-bold">{money(totals.grandTotal)}</td></tr>
                                                <tr><td className="text-muted">Payments Already Made</td><td className="text-end">{money(totals.paid)}</td></tr>
                                                <tr className="border-top"><td className="fw-bold">Remaining Liability</td><td className="text-end fw-bold">{money(totals.remaining)}</td></tr>
                                            </tbody></Table>
                                            <h6 className="text-muted text-uppercase small">Status</h6>
                                            <div className="d-flex flex-wrap gap-3">
                                                <span>Received: {yesNo(pick(header, "itemsRecvd", "ItemsRecvd"))}</span>
                                                <span>Paid: {yesNo(pick(header, "paymentIssued", "PaymentIssued"))}</span>
                                                <span>Attachment: {yesNo(attachments.length > 0)}</span>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                )}
                            </TabPane>

                            <TabPane tabId="lines">
                                {(viewerCanReceive || viewerCanFinanceEdit) && lines.length > 0 && (
                                    <div className="mb-2 d-flex justify-content-between align-items-center">
                                        <div className="text-muted small">
                                            {isApproved && viewerCanFinanceEdit
                                                ? "Editing a line value on an APPROVED EPO updates it in place (it does not re-route for approval)."
                                                : ""}
                                        </div>
                                        {viewerCanReceive && (
                                            <Button size="sm" color="primary" disabled={busy === "receiveAll"} onClick={receiveAll}>{busy === "receiveAll" ? "…" : "Receive All Items"}</Button>
                                        )}
                                    </div>
                                )}
                                {/* Option A (clarity): when the EPO as a whole is marked Paid, the
                                    per-line "Line Paid" flags below can still read No — that flag
                                    tracks payment of each individual line (e.g. partial payments, or
                                    lines intentionally skipped), which is separate from the EPO-level
                                    payment. Explain that here so the two don't look contradictory. */}
                                {isPaid && (
                                    <div className="alert alert-info py-2 px-3 small mb-2 d-flex align-items-start" role="note">
                                        <i className="ri-information-line me-2 mt-1" />
                                        <div>
                                            This EPO is marked <strong>Paid</strong> at the EPO level. The
                                            <strong> Line Paid</strong> column below tracks payment of each
                                            line item separately &mdash; lines may show <em>No</em> if they
                                            were settled together with the EPO or intentionally skipped.
                                            Per-line payments are managed on the <strong>Payments</strong> tab.
                                        </div>
                                    </div>
                                )}
                                <div className="table-responsive epo-sticky-head">
                                    <Table bordered size="sm" className="align-middle mb-0">
                                        <thead className="table-light"><tr>
                                            <th>#</th><th>Description</th><th>Company Category</th><th>Dept. Category</th><th>Expense Category</th>
                                            <th className="text-end">Qty</th><th>UOM</th><th className="text-end">Unit Price</th>
                                            <th className="text-end">Tax %</th><th className="text-end">Disc %</th>
                                            <th className="text-end">Extended</th><th>Asset</th><th>RD</th>
                                            <th className="text-end">Qty Rcvd</th><th className="text-end">Qty Rem</th><th>Status</th>
                                            <th>Rcvd On</th><th>Rcvd By</th>
                                            <th title="Payment status of THIS line item (separate from the EPO-level Paid flag). Manage on the Payments tab.">Line Paid</th>
                                            {(viewerCanReceive || viewerCanFinanceEdit) && <th></th>}
                                        </tr></thead>
                                        <tbody>
                                            {lines.length === 0 ? (
                                                <tr><td colSpan={(viewerCanReceive || viewerCanFinanceEdit) ? 20 : 19} className="text-muted">No line items.</td></tr>
                                            ) : lines.map((ln, idx) => {
                                                const lnNum = pick(ln, "lineNum", "LineNum") ?? idx + 1;
                                                const recvd = pick(ln, "recvd", "Recvd");
                                                const editing = editLineNum === lnNum;
                                                if (editing && editLine) {
                                                    return (
                                                        <tr key={lnNum} className="table-warning">
                                                            <td>{lnNum}</td>
                                                            <td><Input bsSize="sm" value={editLine.description} onChange={(e) => setEditLine({ ...editLine, description: e.target.value })} /></td>
                                                            <td><Input bsSize="sm" value={editLine.productGroup} onChange={(e) => setEditLine({ ...editLine, productGroup: e.target.value })} /></td>
                                                            <td><Input bsSize="sm" value={editLine.dept} onChange={(e) => setEditLine({ ...editLine, dept: e.target.value })} /></td>
                                                            <td><Input bsSize="sm" value={editLine.acct} onChange={(e) => setEditLine({ ...editLine, acct: e.target.value })} /></td>
                                                            <td><Input bsSize="sm" type="number" value={editLine.quan} onChange={(e) => setEditLine({ ...editLine, quan: e.target.value })} /></td>
                                                            <td><Input bsSize="sm" value={editLine.uom} onChange={(e) => setEditLine({ ...editLine, uom: e.target.value })} /></td>
                                                            <td><Input bsSize="sm" type="number" value={editLine.unitPrice} onChange={(e) => setEditLine({ ...editLine, unitPrice: e.target.value })} /></td>
                                                            <td><Input bsSize="sm" type="number" value={editLine.taxPercent} onChange={(e) => setEditLine({ ...editLine, taxPercent: e.target.value })} /></td>
                                                            <td><Input bsSize="sm" type="number" value={editLine.discountPercent} onChange={(e) => setEditLine({ ...editLine, discountPercent: e.target.value })} /></td>
                                                            <td className="text-end">—</td>
                                                            <td><Input type="checkbox" checked={editLine.asset} onChange={(e) => setEditLine({ ...editLine, asset: e.target.checked })} /></td>
                                                            <td><Input type="checkbox" checked={editLine.rd} onChange={(e) => setEditLine({ ...editLine, rd: e.target.checked })} /></td>
                                                            <td className="text-end">{pick(ln, "qtyRec", "QtyRec") ?? 0}</td>
                                                            <td className="text-end">{Math.max(0, Number(pick(ln, "quan", "Quan") || 0) - Number(pick(ln, "qtyRec", "QtyRec") || 0))}</td>
                                                            <td>{lineRecvStatusBadge(ln)}</td>
                                                            <td>{fmtDate(pick(ln, "dateTimeRec", "DateTimeRec"))}</td>
                                                            <td>{recvdByLabel(ln)}</td>
                                                            <td>{yesNo(pick(ln, "paid", "Paid"))}</td>
                                                            <td className="text-nowrap">
                                                                <Button size="sm" color="link" className="p-0 me-2" disabled={busy === `editln-${lnNum}`} onClick={() => saveEditLine(lnNum)}>Save</Button>
                                                                <Button size="sm" color="link" className="p-0 text-muted" onClick={cancelEditLine}>Cancel</Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                                return (
                                                    <tr key={lnNum}>
                                                        <td>{lnNum}</td>
                                                        <td>{pick(ln, "description", "Description") || "—"}</td>
                                                        <td>{pick(ln, "productGroup", "ProductGroup") || "—"}</td>
                                                        <td>{pick(ln, "dept", "Dept") || "—"}</td>
                                                        <td>{pick(ln, "acct", "Acct") || "—"}</td>
                                                        <td className="text-end">{pick(ln, "quan", "Quan") ?? "—"}</td>
                                                        <td>{pick(ln, "uom", "Uom") || "—"}</td>
                                                        <td className="text-end">{money(pick(ln, "unitPrice", "UnitPrice"))}</td>
                                                        <td className="text-end">{Number(pick(ln, "taxPercent", "TaxPercent") || 0)}</td>
                                                        <td className="text-end">{Number(pick(ln, "discountPercent", "DiscountPercent") || 0)}</td>
                                                        <td className="text-end">{money(legacyAwareLineTotal(ln, header))}</td>
                                                        <td>{yesNo(pick(ln, "asset", "Asset"))}</td>
                                                        <td>{yesNo(pick(ln, "rd", "Rd"))}</td>
                                                        {/* Batch 1 / Round 3 — Qty Received / Remaining / Status */}
                                                        <td className="text-end">{pick(ln, "qtyRec", "QtyRec") ?? 0}</td>
                                                        <td className="text-end">{Math.max(0, Number(pick(ln, "quan", "Quan") || 0) - Number(pick(ln, "qtyRec", "QtyRec") || 0))}</td>
                                                        <td>{lineRecvStatusBadge(ln)}</td>
                                                        <td>{fmtDate(pick(ln, "dateTimeRec", "DateTimeRec"))}</td>
                                                        <td>{recvdByLabel(ln)}</td>
                                                        <td>{yesNo(pick(ln, "paid", "Paid"))}</td>
                                                        {(viewerCanReceive || viewerCanFinanceEdit) && (
                                                            <td className="text-nowrap" style={{ minWidth: 230 }}>
                                                                {viewerCanFinanceEdit && (
                                                                    <Button size="sm" color="link" className="p-0 me-2" onClick={() => startEditLine(ln)}>Edit</Button>
                                                                )}
                                                                {viewerCanFinanceEdit && lines.length > 1 && (
                                                                    <Button size="sm" color="link" className="p-0 me-2 text-danger" disabled={busy === `delln-${lnNum}`} onClick={() => deleteLine(lnNum)}>{busy === `delln-${lnNum}` ? "…" : "Delete"}</Button>
                                                                )}
                                                                {/* Batch 1 / Round 3 — inline partial receive (synced with the
                                                                    Receive page). Shows a qty box + comment box + Receive while
                                                                    the line still has remaining qty; once fully received it
                                                                    offers Reopen. The server enforces all rules. */}
                                                                {viewerCanReceive && !recvd && (() => {
                                                                    const ordered = Number(pick(ln, "quan", "Quan") || 0);
                                                                    const already = Number(pick(ln, "qtyRec", "QtyRec") || 0);
                                                                    const remaining = Math.max(0, ordered - already);
                                                                    const d = recvDraft[lnNum] || {};
                                                                    const busyRow = busy === `recvqty-${lnNum}` || busy === `recv-${lnNum}`;
                                                                    return (
                                                                        <div className="d-inline-flex flex-column gap-1 align-top">
                                                                            <div className="d-flex gap-1 align-items-center">
                                                                                <Input
                                                                                    type="number" bsSize="sm" min={1} max={remaining > 0 ? remaining : undefined} step={1}
                                                                                    style={{ width: 70 }}
                                                                                    placeholder="Qty"
                                                                                    value={d.qty ?? ""}
                                                                                    disabled={busyRow}
                                                                                    onChange={(e) => setRecvQty(lnNum, e.target.value)}
                                                                                    title={`Remaining: ${remaining}`}
                                                                                />
                                                                                <Button size="sm" color="success" outline disabled={busyRow}
                                                                                    onClick={() => receiveQtyLine(lnNum, ordered, already)}>
                                                                                    {busyRow ? "…" : "Receive"}
                                                                                </Button>
                                                                            </div>
                                                                            <Input
                                                                                type="text" bsSize="sm"
                                                                                style={{ minWidth: 200 }}
                                                                                placeholder="Comment (required if partial)"
                                                                                value={d.comment ?? ""}
                                                                                disabled={busyRow}
                                                                                onChange={(e) => setRecvComment(lnNum, e.target.value)}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })()}
                                                                {viewerCanReceive && recvd && (
                                                                    <Button size="sm" color="link" className="p-0 text-warning" disabled={busy === `reopen-${lnNum}`} onClick={() => reopenLine(lnNum)}>{busy === `reopen-${lnNum}` ? "…" : "Reopen"}</Button>
                                                                )}
                                                            </td>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Batch 2 / Round 5 — receiving history with comments. Same
                                    data (GET epo/{n}/receive/log) the dedicated Receive page
                                    shows, so the two screens stay consistent. */}
                                {lines.length > 0 && (
                                    <div className="mt-3">
                                        <div className="small text-muted mb-1">Receiving History</div>
                                        {recvLog.length === 0 ? (
                                            <div className="text-muted small">No receiving activity yet.</div>
                                        ) : (
                                            <Table size="sm" className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>When</th><th>Line</th><th className="text-end">Qty</th>
                                                        <th>Event</th><th>Comment</th><th>Received By</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {recvLog.map((e, i) => (
                                                        <tr key={`${pick(e, "id", "Id") ?? i}-${i}`}>
                                                            <td>{fmtDateTime(pick(e, "receivedOn", "ReceivedOn"))}</td>
                                                            <td>{pick(e, "lineNum", "LineNum") ?? "—"}</td>
                                                            <td className="text-end">{pick(e, "qtyReceived", "QtyReceived") ?? "—"}</td>
                                                            <td>{recvEventLabel(pick(e, "eventType", "EventType"))}</td>
                                                            <td className="text-muted">{pick(e, "comment", "Comment") || "—"}</td>
                                                            <td>{pick(e, "receivedByName", "ReceivedByName") || pick(e, "receivedBy", "ReceivedBy") || "—"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </div>
                                )}
                            </TabPane>

                            <TabPane tabId="attachments">
                                {permissions.canManageAttachments && (
                                    <div className="mb-2">
                                        <Label className="form-label small text-muted">Add attachment (PDF, Office, image, ≤15 MB)</Label>
                                        <Input type="file" bsSize="sm" disabled={busy === "upload"} onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAttachment(f); e.target.value = ""; }} />
                                    </div>
                                )}
                                <Table bordered size="sm" className="mb-0">
                                    <thead className="table-light"><tr><th>File</th><th>Uploaded By</th><th>Date</th><th></th></tr></thead>
                                    <tbody>
                                        {attachments.length === 0 ? (
                                            <tr><td colSpan={4} className="text-muted">No attachments on this EPO.</td></tr>
                                        ) : attachments.map((a) => (
                                            <tr key={pick(a, "id", "Id")}>
                                                <td>{pick(a, "fileName", "FileName")}</td>
                                                <td>{pick(a, "author", "Author")}</td>
                                                <td>{fmtDate(pick(a, "timestamp", "Timestamp"))}</td>
                                                <td className="text-nowrap">
                                                    <Button size="sm" color="link" className="p-0 me-2" onClick={() => downloadAttachment(a)}>Download</Button>
                                                    {permissions.canManageAttachments && (<Button size="sm" color="link" className="p-0 text-danger" disabled={busy === "delAtt"} onClick={() => deleteAttachment(a)}>Delete</Button>)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </TabPane>

                            <TabPane tabId="comments">
                                {tabLoading ? (
                                    <div className="d-flex align-items-center gap-2 py-3"><Spinner size="sm" /> <span className="text-muted">Loading comments…</span></div>
                                ) : (
                                    <>
                                        <Table bordered size="sm" className="mb-2">
                                            <thead className="table-light"><tr><th>Author</th><th>When</th><th>Comment</th><th></th></tr></thead>
                                            <tbody>
                                                {comments.length === 0 ? (
                                                    <tr><td colSpan={4} className="text-muted">No comments.</td></tr>
                                                ) : comments.map((r) => {
                                                    const cid = pick(r, "id", "Id");
                                                    const author = String(pick(r, "empid", "empId", "EmpId") || "");
                                                    const mine = author === me;
                                                    const isComment = String(pick(r, "status", "Status") || "").toUpperCase() === "COMMENT";
                                                    return (
                                                        <tr key={cid}>
                                                            <td>{author ? (nameCache[author] || author) : "—"}</td>
                                                            <td>{fmtDateTime(pick(r, "stamp", "Stamp"))}</td>
                                                            <td>
                                                                {editingId === cid ? (
                                                                    <Input bsSize="sm" value={editText} onChange={(e) => setEditText(e.target.value)} />
                                                                ) : (pick(r, "comment", "Comment"))}
                                                            </td>
                                                            <td className="text-nowrap">
                                                                {isComment && mine && (
                                                                    editingId === cid ? (
                                                                        <>
                                                                            <Button size="sm" color="link" className="p-0 me-2" disabled={busy === "editComment"} onClick={saveEditComment}>Save</Button>
                                                                            <Button size="sm" color="link" className="p-0 text-muted" onClick={cancelEditComment}>Cancel</Button>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Button size="sm" color="link" className="p-0 me-2" onClick={() => startEditComment(r)}>Edit</Button>
                                                                            <Button size="sm" color="link" className="p-0 text-danger" disabled={busy === `delc-${cid}`} onClick={() => deleteComment(r)}>Delete</Button>
                                                                        </>
                                                                    )
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                        {permissions.canComment !== false && (
                                            <Row className="g-2 align-items-end">
                                                <Col md={9}><Input bsSize="sm" placeholder="Add a comment…" value={newComment} onChange={(e) => setNewComment(e.target.value)} /></Col>
                                                <Col md={3}><Button color="primary" size="sm" className="w-100" disabled={busy === "addComment"} onClick={addComment}>{busy === "addComment" ? "…" : "Add Comment"}</Button></Col>
                                            </Row>
                                        )}
                                        <div className="text-muted small mt-2">
                                            You can edit or delete your own comments. Approval-log entries are immutable.
                                        </div>
                                    </>
                                )}
                            </TabPane>

                            <TabPane tabId="approvalLog">
                                {tabLoading ? (
                                    <div className="d-flex align-items-center gap-2 py-3"><Spinner size="sm" /> <span className="text-muted">Loading approval log…</span></div>
                                ) : (
                                    <Table bordered size="sm" className="mb-0">
                                        {/* Round 5 batch — the log shows WHO each requirement belonged
                                            to (Approver) and WHO acted (By), with names resolved
                                            server-side (one EmpBasic join); "Unknown Employee (id)"
                                            only when an id no longer resolves. */}
                                        <thead className="table-light"><tr><th>Step</th><th>Approver</th><th>Action</th><th>By</th><th className="text-end">Amount</th><th>When</th><th>Active</th><th>Comment</th></tr></thead>
                                        <tbody>
                                            {history.length === 0 ? (
                                                <tr><td colSpan={8} className="text-muted">No approval activity.</td></tr>
                                            ) : history.map((r, idx) => {
                                                const rowApproverName = pick(r, "approverName", "ApproverName");
                                                const rowApproverId = pick(r, "approverEmpId", "ApproverEmpId");
                                                const rowActorName = pick(r, "actorName", "ActorName");
                                                const rowActorId = pick(r, "actorEmpId", "ActorEmpId");
                                                const rowRequesterName = pick(r, "requesterName", "RequesterName");
                                                const rowReqId = String(pick(r, "empid", "empId", "EmpId") || "").trim();
                                                const approverCell = rowApproverName
                                                    ? `${rowApproverName} (${rowApproverId ?? ""})`.replace(" ()", "")
                                                    : (rowApproverId != null && rowApproverId !== ""
                                                        ? `Unknown Employee (${rowApproverId})`
                                                        : "—");
                                                const byCell = rowActorName
                                                    || (rowActorId != null && rowActorId !== "" ? `Unknown Employee (${rowActorId})` : "")
                                                    || rowRequesterName
                                                    || nameCache[rowReqId]
                                                    || rowReqId
                                                    || "—";
                                                return (
                                                    <tr key={pick(r, "id", "Id") ?? idx}>
                                                        <td>{pick(r, "stepNumber", "StepNumber") ?? "—"}</td>
                                                        <td>{approverCell}</td>
                                                        <td>{statusBadge(pick(r, "status", "Status"))}</td>
                                                        <td>{byCell}</td>
                                                        <td className="text-end">{pick(r, "amount", "Amount") != null ? money(pick(r, "amount", "Amount")) : "—"}</td>
                                                        <td>{fmtDateTime(pick(r, "stamp", "Stamp"))}</td>
                                                        <td>{yesNo(pick(r, "active", "Active"))}</td>
                                                        <td>{pick(r, "comment", "Comment") || "—"}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                )}
                            </TabPane>

                            <TabPane tabId="payments">
                                {tabLoading ? (
                                    <div className="d-flex align-items-center gap-2 py-3"><Spinner size="sm" /> <span className="text-muted">Loading payments…</span></div>
                                ) : (
                                    <>
                                        <div className="mb-2">
                                            <span className="text-muted me-3">Grand Total: <span className="fw-semibold">{money(totals.grandTotal)}</span></span>
                                            <span className="text-muted me-3">Paid: <span className="fw-semibold">{money(totals.paid)}</span></span>
                                            <span className="text-muted">Remaining Liability: <span className="fw-semibold">{money(totals.remaining)}</span></span>
                                        </div>

                                        {/* Legacy layout: pick a line up top, then its payments + add form below */}
                                        <div className="small text-muted mb-1">Line Items <span className="text-muted">(click a line to manage its payments)</span></div>
                                        <Table bordered hover size="sm" className="mb-3">
                                            {/* Round EPO-Pay-1 (owner request #7): the payments grid now
                                                carries the SAME line detail as the Line Items tab (Prod
                                                Group, Dept, Account, Qty, UOM, Unit Price, Tax %, Disc %,
                                                Extended) plus the payment columns, so Finance can pay a
                                                line without tab-hopping to identify it. */}
                                            <thead className="table-light"><tr>
                                                <th>L#</th><th>Description</th>
                                                <th>Company Category</th><th>Dept. Category</th><th>Expense Category</th>
                                                <th className="text-end">Qty</th><th>UOM</th>
                                                <th className="text-end">Unit Price</th>
                                                <th className="text-end">Tax %</th><th className="text-end">Disc %</th>
                                                <th className="text-end">Ext. Cost</th><th className="text-end">Paid</th><th>Payment Applied</th>
                                            </tr></thead>
                                            <tbody>
                                                {lines.length === 0 ? (
                                                    <tr><td colSpan={13} className="text-muted">No line items.</td></tr>
                                                ) : lines.map((l, i) => {
                                                    const n = pick(l, "lineNum", "LineNum") ?? i + 1;
                                                    const ec = legacyAwareLineTotal(l, header);   // Go-live parity (2026-09-24): EPO#S16
                                                    const linePaid = payments
                                                        .filter((p) => String(pick(p, "lineNum", "LineNum")) === String(n) && !pick(p, "void", "Void"))
                                                        .reduce((a, p) => a + Number(pick(p, "amount", "Amount") || 0), 0);
                                                    const selected = String(payLine) === String(n);
                                                    return (
                                                        <tr key={n} className={selected ? "table-primary" : ""} style={{ cursor: "pointer" }}
                                                            onClick={() => { setPayLine(String(n)); const rem = +(ec - linePaid).toFixed(2); setPayAmount(rem !== 0 ? String(rem) : ""); }}>
                                                            <td>{n}</td>
                                                            <td>{pick(l, "description", "Description") || "—"}</td>
                                                            <td>{pick(l, "productGroup", "ProductGroup", "prodGroup", "ProdGroup") || "—"}</td>
                                                            <td>{pick(l, "dept", "Dept") || "—"}</td>
                                                            <td>{pick(l, "acct", "Acct", "accountNo", "AccountNo") || "—"}</td>
                                                            <td className="text-end">{pick(l, "quan", "Quan", "quantity", "Quantity") ?? "—"}</td>
                                                            <td>{pick(l, "uom", "Uom", "UOM") || "—"}</td>
                                                            <td className="text-end">{money(Number(pick(l, "unitPrice", "UnitPrice") || 0))}</td>
                                                            <td className="text-end">{Number(pick(l, "taxPercent", "TaxPercent", "tax", "Tax") || 0)}</td>
                                                            <td className="text-end">{Number(pick(l, "discountPercent", "DiscountPercent") || 0)}</td>
                                                            <td className="text-end">{money(ec)}</td>
                                                            <td className="text-end">{money(linePaid)}</td>
                                                            <td>{linePaid >= ec && ec > 0 ? <Badge color="success">Fully paid</Badge> : (linePaid !== 0 ? <Badge color="info">Partial</Badge> : <span className="text-muted">—</span>)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>

                                        <div className="small text-muted mb-1">
                                            Payments for Highlighted Line{payLine ? <> — <span className="fw-semibold">Line #{payLine}</span></> : ""}
                                        </div>
                                        <Table bordered size="sm" className="mb-3">
                                            <thead className="table-light"><tr>
                                                <th>Line</th><th className="text-end">Amount</th><th>Note</th><th>Paid By</th><th>When</th><th>Status</th>
                                                {permissions.canPay && <th></th>}
                                            </tr></thead>
                                            <tbody>
                                                {(() => {
                                                    const rows = payments.filter((p) => !payLine || String(pick(p, "lineNum", "LineNum")) === String(payLine));
                                                    if (rows.length === 0) {
                                                        return <tr><td colSpan={permissions.canPay ? 7 : 6} className="text-muted">{payLine ? "No payments recorded for this line." : "Select a line above to view its payments."}</td></tr>;
                                                    }
                                                    return rows.map((p) => {
                                                        const pid = pick(p, "id", "Id");
                                                        const voided = pick(p, "void", "Void");
                                                        return (
                                                            <tr key={pid} className={voided ? "text-muted" : ""}>
                                                                <td>{pick(p, "lineNum", "LineNum")}</td>
                                                                <td className="text-end">{money(pick(p, "amount", "Amount"))}</td>
                                                                <td>{pick(p, "note", "Note") || "—"}</td>
                                                                <td>{pick(p, "paidByName", "PaidByName") || pick(p, "paidBy", "PaidBy")}</td>
                                                                <td>{fmtDateTime(pick(p, "timestamp", "Timestamp"))}</td>
                                                                <td>{voided ? <Badge color="secondary">Void</Badge> : <Badge color="success">Active</Badge>}</td>
                                                                {permissions.canPay && (<td>{!voided && (<Button size="sm" color="link" className="p-0 text-danger" disabled={busy === `void-${pid}`} onClick={() => voidPayment(pid)}>{busy === `void-${pid}` ? "…" : "Void"}</Button>)}</td>)}
                                                            </tr>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </Table>

                                        {permissions.canPay && (
                                            <div className="border rounded p-3 bg-light">
                                                <div className="fw-semibold mb-2 small text-muted text-uppercase">Add New Payment for Highlighted Line Item</div>
                                                {lines.length === 0 ? (
                                                    <div className="text-muted small">This EPO has no line items to pay.</div>
                                                ) : !payLine ? (
                                                    <div className="text-muted small">Click a line in the grid above to add a payment to it.</div>
                                                ) : (
                                                <Row className="g-2 align-items-end">
                                                    <Col md={2}><Label className="small text-muted mb-1">Line</Label><Input bsSize="sm" value={`#${payLine}`} disabled /></Col>
                                                    <Col md={3}><Label className="small text-muted mb-1">Amount</Label><Input bsSize="sm" type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} /></Col>
                                                    <Col md={5}><Label className="small text-muted mb-1">Note <span className="text-danger">*</span></Label><Input bsSize="sm" value={payNote} onChange={(e) => setPayNote(e.target.value)} /></Col>
                                                    <Col md={2}><Button color="primary" size="sm" className="w-100" disabled={busy === "addPay"} onClick={addPayment}>{busy === "addPay" ? "…" : "Add"}</Button></Col>
                                                </Row>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </TabPane>
                        </TabContent>
                    </>
                )}
            </ModalBody>

            <ModalFooter className="d-flex flex-wrap gap-2 justify-content-between">
                <div className="d-flex flex-wrap gap-2">
                    {permissions.canApprove && iAmPendingApprover && (
                        <>
                            <Button color="success" size="sm" disabled={busy === "approve"} onClick={approve}>{busy === "approve" ? "…" : "Approve"}</Button>
                            <Button color="danger" size="sm" disabled={busy === "reject"} onClick={reject}>{busy === "reject" ? "…" : "Reject"}</Button>
                        </>
                    )}
                    {permissions.canPay && isApproved && (
                        <label className="d-inline-flex align-items-center gap-2 mb-0" style={{ cursor: busy === "markPaid" ? "wait" : "pointer" }}>
                            <Input
                                type="checkbox"
                                checked={isPaid}
                                disabled={busy === "markPaid"}
                                onChange={(e) => setProcessed(e.target.checked)}
                            />
                            <span className="fw-semibold">{`Mark EPO #${epoNumber} as Processed`}</span>
                        </label>
                    )}
                    {/* Rounds 2 batch — Submit for DRAFTs / legacy unrouted PENDING. */}
                    {isSubmittableHere && (
                        <Button color="success" size="sm" disabled={busy === "submitEpo"} onClick={submitEpo}>{busy === "submitEpo" ? "…" : "Submit for Approval"}</Button>
                    )}
                    {/* Cancel — creator only, PENDING only (server enforces both); shown
                        only when the legacy Void is not available (a step already approved). */}
                    {iAmCreator && isPending && !viewerCanVoid && (
                        <Button color="warning" size="sm" disabled={busy === "cancelEpo"} onClick={cancelEpo}>{busy === "cancelEpo" ? "…" : "Cancel EPO"}</Button>
                    )}
                    {/* Void — owner 2026-10-16, as legacy: anyone who can open the EPO,
                        not after payment, not already void, not mid-approval. */}
                    {(viewerCanVoid || (viewerCanFinanceEdit && !isPaid && !isVoid)) && (
                        <Button color="danger" size="sm" disabled={busy === "voidEpo"} onClick={voidEpo}>{busy === "voidEpo" ? "…" : "Void EPO"}</Button>
                    )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <Button color="light" size="sm" disabled={printing} onClick={printEpo}>{printing ? "Preparing…" : "Print"}</Button>
                    <Button color="light" onClick={toggle}>Close</Button>
                </div>
            </ModalFooter>
        </Modal>
    );
}
